import { useState, useEffect, useLayoutEffect } from 'react'

const applyMetaColor = (color: string) => {
  const metas = document.getElementsByTagName('meta')
  for (let i = 0; i < metas.length; i += 1) {
    if (metas.item(i).name === 'theme-color') {
      metas.item(i).content = color
    }
  }
}

const applyTheme = (isDark: boolean) => {
  const systemIsDark = window.matchMedia('(prefers-color-scheme: dark)').matches
  console.info('systemIsDark applied theme', systemIsDark, isDark)
  if (systemIsDark) {
    isDark = true
  } else {
    isDark = false
  }
  const theme = isDark ? 'dark' : 'light'
  const root = document.documentElement
  const body = document.body

  // Set cookie with proper attributes
  document.cookie = `theme=${theme}; path=/; max-age=31536000; SameSite=Lax`

  // Handle classList in a more reliable way
  if (isDark) {
    root.classList.add('dark')
    body.classList.add('dark')
  } else {
    root.classList.remove('dark')
    body.classList.remove('dark')
  }

  // Set consistent theme attributes
  root.style.colorScheme = theme
  root.dataset.theme = theme
  body.dataset.theme = theme

  // Match the product canvas (--background), NOT pure black/white — the
  // OS status-bar / browser chrome tints to this, so pure #000/#fff read
  // as a hard band above the #1a1f19 / #f7f1e7 app and break the seamless
  // look. This runs on every theme apply, so it OVERRIDES index.html's
  // static metas at runtime: both must move together, along with
  // --background in new-ui-theme.css (dark = forest green, July 2026
  // rebrand; light = warm stone, Aug 2026).
  applyMetaColor(isDark ? '#1a1f19' : '#f7f1e7')

  console.info(
    'applied theme',
    theme,
    window.matchMedia('(prefers-color-scheme: dark)').matches
  )
}

const THEME_COOKIE = 'theme'
const DARK_CLASS = 'dark'
const COLORS = {
  dark: '#1a1f19',
  light: '#f7f1e7',
} as const

const getThemeFromCookie = () => {
  const cookies = document.cookie.split(';')
  const themeCookie = cookies
    .find((cookie: any) => cookie.trim().startsWith('theme='))
    ?.split('=')[1]

  return themeCookie === 'dark' ? 'dark' : 'light'
}

export const useColorMode = () => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const cookieTheme = getThemeFromCookie()
    // If no valid cookie theme, use system preference
    if (cookieTheme === 'dark' || cookieTheme === 'light') {
      return cookieTheme === 'dark'
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches
  })

  useEffect(() => {
    applyTheme(isDarkMode)
  }, [isDarkMode])

  // System theme change detection
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    const handleChange = (e: MediaQueryListEvent) => {
      // Only update if no cookie exists (user hasn't set preference)

      setIsDarkMode(e.matches)
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => {
      mediaQuery.removeEventListener('change', handleChange)
    }
  }, [])

  return [isDarkMode, setIsDarkMode] as const
}
